import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, CaseSensitive, FileImage } from 'lucide-react';
import { submitStructuredData } from '../services/LLMCalls';
import GetDescription from './GetDescription';
import ProvideReference from './ProvideReference';

const CHAR_LIMITS = {
  title: { max: 100 },
  instructions: { max: 20000 }
};

const UploadWireframe = ({ projectId }) => {
  const [formData, setFormData] = useState({
    title: '',
    instructions: '',
    reference: null
  });
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReferenceChange = (reference) => {
    setFormData((prev) => ({
      ...prev,
      reference
    }));
  };

  // Update the instructions field from the GetDescription component
  const handleInstructionsUpdate = (value) => {
    setFormData((prev) => ({
      ...prev,
      instructions: value
    }));
  };

  useEffect(() => {
    // Only validate required fields (title and instructions), reference is optional
    const titleValid = formData.title.trim().length > 0 && formData.title.length <= CHAR_LIMITS.title.max;
    const instructionsValid = formData.instructions.trim().length > 0 && formData.instructions.length <= CHAR_LIMITS.instructions.max;
    
    setIsValid(titleValid && instructionsValid);
  }, [formData]);

  const getCharacterCount = (field) => {
    const count = formData[field].length;
    const { max } = CHAR_LIMITS[field];
    if (count > max) {
      return `${count - max} characters over limit`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    
    try {
      // Format the data in the structure expected by submitStructuredData
      // This needs to match what structured.js would create
      const structuredFormData = {
        title: formData.title,
        // Instructions will be passed as content
        content: formData.instructions,
        // Add any additional required fields with default values
        layout: 'single-page',
        components: [
          {
            id: Date.now(),
            content: formData.instructions,
            section: 'main'
          }
        ],
        reference: formData.reference // Include reference (optional)
      };
      
      // Navigate to status page immediately
      navigate('/prompt-status', { 
        state: { 
          projectId,
          isProcessing: true 
        }
      });

      // Use submitStructuredData which properly formats the request with view_title and ui_description
      const response = await submitStructuredData(projectId, structuredFormData);
      
      // Update status page with promptId
      navigate('/prompt-status', {
        state: {
          projectId,
          promptId: response.promptId,
          isProcessing: true
        },
        replace: true
      });

    } catch (error) {
      console.error('Error:', error);
      navigate('/prompt-status', {
        state: {
          projectId,
          error: 'Failed to submit instructions. Please try again.'
        },
        replace: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-8'>
      {/* Title Section */}
      <div className='space-y-4'>
        <div className='space-y-2'>
          <div className='flex items-center gap-3 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg p-7 shadow-md mb-4'>
            <div className='flex items-center justify-center w-7 h-7'>
              <CaseSensitive className='w-5 h-5 text-gray-600' />
            </div>
            <div>
              <label className='text-lg font-semibold text-gray-800'>
                Title
              </label>
              <p className='text-gray-500'>Enter a title for the view you would like to generate.</p>
            </div>
          </div>
        </div>
        <div>
          <input
            type='text'
            name='title'
            value={formData.title}
            onChange={handleChange}
            placeholder='Descriptive title for your view...'
            className='w-full p-3 border rounded-md'
            disabled={isSubmitting}
          />
          {getCharacterCount('title') && <p className='text-orange-500 text-sm'>{getCharacterCount('title')}</p>}
        </div>
      </div>

      {/* Wireframe Upload Section */}
      <div className='space-y-4'>
        <div className='space-y-2'>
          <div className='flex items-center gap-3 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg p-7 shadow-md mb-4'>
            <div className='flex items-center justify-center w-7 h-7'>
              <FileImage className='w-5 h-5 text-gray-600' />
            </div>
            <div>
              <label className='text-lg font-semibold text-gray-800'>
                Upload Wireframe
              </label>
              <p className='text-gray-500'>Upload a wireframe, mockup, or screenshot to automatically generate UI description.</p>
            </div>
          </div>
        </div>
        
        {/* GetDescription component to handle the image upload and processing */}
        <GetDescription 
          onDescriptionGenerated={handleInstructionsUpdate}
          initialValue={formData.instructions}
          projectId={projectId}
          disabled={isSubmitting}
        />
        
        {getCharacterCount('instructions') && <p className='text-orange-500 text-sm'>{getCharacterCount('instructions')}</p>}
      </div>
      
      {/* ProvideReference Component */}
      <ProvideReference 
        projectId={projectId}
        onReferenceChange={handleReferenceChange}
      />

      {/* Next Step Button */}
      <div className='flex justify-end'>
        <button
          type='submit'
          disabled={!isValid || isSubmitting}
          className={`flex items-center gap-2 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed`}
        >
          {isSubmitting ? (
            <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white'></div>
          ) : (
            <Sparkles className='w-5 h-5' />
          )}
          {isSubmitting ? 'Processing...' : 'Next Step'}
        </button>
      </div>
    </form>
  );
};

export default UploadWireframe;